import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { Modal, Table, TableFilterModel } from 'design-library';

import { IExpert, IJobPost } from 'common/models';
import { getCurrencyFormat, getDateFormat } from 'common/utils';

import { usePostedJobs } from 'admin/hooks';
import { PostedJobManagementServices } from 'admin/services';
import { JobTypeEnum, getEnumKeys } from 'common/enums';
import { ApplicationCountCell, ApplicationDrawer } from '../applications';
import { ExpertProfile } from '../user-management/expert-profile';
import PostedJobActions from './PostedJobActions';

export const PostedJobManagement = () => {
  const [selectedJobPost, setSelectedJobPost] = useState<
    IJobPost | undefined
  >();
  const [selectedApplicant, setSelectedApplicant] = useState<
    IExpert | undefined
  >();

  const {
    isLoadingPostedJobs,
    jobPostResults,
    paginationModel,
    setPaginationModel,
    setQueryOptions,
  } = usePostedJobs();

  const getBudget = (jobPost: IJobPost) =>
    PostedJobManagementServices.calculateJobPostBudget({
      end: jobPost.endTime,
      hourlyRate: jobPost.hourlyRate,
      hoursPerDay: jobPost.hoursPerDay,
      start: jobPost.startTime,
    });

  const getFormattedRows = (jobPosts: IJobPost[]) =>
    jobPosts.map((jobPost) => ({
      actions: jobPost,
      applications: jobPost,
      bidder: jobPost.jobType === JobTypeEnum.PartTime ? jobPost.bidCount : '-',
      budget: jobPost.showBudget ? getCurrencyFormat(getBudget(jobPost)) : '-',
      id: jobPost.id,
      jobType: getEnumKeys(JobTypeEnum)[jobPost.jobType],
      position: jobPost.title,
      postedBy: jobPost.postedBy,
      postedDate: getDateFormat(jobPost.createdAt),
      projectName: jobPost.projectName,
    }));

  const onFilterChange = useCallback(
    (filterModel: TableFilterModel) => {
      setQueryOptions(
        filterModel.items.reduce(
          (acc, item) =>
            item.value ? { ...acc, [item.field]: item.value } : acc,
          {}
        )
      );
    },
    [setQueryOptions]
  );

  return (
    <Box px={4}>
      <Table
        columns={[
          {
            field: 'projectName',
            headerName: 'Job Post Title',
            width: 310,
          },
          {
            field: 'position',
            filterable: false,
            headerName: 'Position',
            width: 160,
          },
          {
            align: 'center',
            field: 'jobType',
            headerAlign: 'center',
            headerName: 'Job Type',
            width: 140,
          },
          {
            field: 'postedBy',
            headerAlign: 'center',
            headerName: 'Posted By',
            width: 200,
          },
          {
            align: 'center',
            field: 'postedDate',
            filterable: false,
            headerAlign: 'center',
            headerName: 'Posted Date',
            width: 150,
          },
          {
            align: 'right',
            field: 'budget',
            filterable: false,
            headerAlign: 'center',
            headerName: 'Budget',
            width: 150,
          },
          {
            align: 'center',
            field: 'bidder',
            filterable: false,
            headerAlign: 'center',
            headerName: 'Bidder',
            width: 150,
          },
          {
            align: 'center',
            field: 'applications',
            filterable: false,
            headerAlign: 'center',
            headerName: 'Applications',
            renderCell: (params) => {
              return (
                <ApplicationCountCell
                  jobPost={params.value}
                  onView={() => setSelectedJobPost(params.value)}
                />
              );
            },
            width: 150,
          },
          {
            align: 'center',
            field: 'actions',
            filterable: false,
            flex: 1,
            headerAlign: 'center',
            headerName: 'Actions',
            minWidth: 350,
            renderCell: (params) => <PostedJobActions jobPost={params.value} />,
          },
        ]}
        loading={isLoadingPostedJobs}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        paginationModel={paginationModel}
        pageSizeOptions={[paginationModel.pageSize]}
        rowCount={jobPostResults.total}
        rows={getFormattedRows(jobPostResults.data)}
        showToolbar
        slots={
          {
            // noRowsOverlay: () => <BidTableEmptyState />,
          }
        }
        filterMode="server"
        onFilterModelChange={onFilterChange}
      />
      {selectedJobPost && (
        <ApplicationDrawer
          open
          jobPost={selectedJobPost}
          onClose={() => setSelectedJobPost(undefined)}
          onSelectApplicant={setSelectedApplicant}
        />
      )}
      {selectedApplicant && (
        <Modal isShown onCancel={() => setSelectedApplicant(undefined)}>
          <ExpertProfile readOnly expert={selectedApplicant} />
        </Modal>
      )}
    </Box>
  );
};
